import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, TrendingUp } from 'lucide-react';
import { useLocalization } from '@/hooks/useLocalization';

const RADIUS = 54;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const scoreColor = (s: number) =>
  s >= 80 ? '#10b981' : s >= 60 ? '#f59e0b' : '#ef4444';

const scoreLabel = (s: number) =>
  s >= 80 ? 'Good' : s >= 60 ? 'Needs Attention' : 'At Risk';

export const GovernanceScore = () => {
  const { tx, t } = useLocalization();
  const { tenantId } = useAuth();
  const [score, setScore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // tenantId may legitimately be null (single-tenant/dev mode, or the
    // custom access-token hook not stamping claims — see Sprint I.1). Still
    // fire the query rather than hanging forever on loading=true; the RPC's
    // WHERE tenant_id = p_tenant_id just matches nothing when null, which
    // resolves to a defined score via its own COALESCE fallback.
    setLoading(true);
    supabase
      .rpc('compute_governance_score', { p_tenant_id: tenantId })
      .then(({ data, error }) => {
        if (!error && data !== null) setScore(data as number);
        setLoading(false);
      });
  }, [tenantId]);

  const dashOffset = score !== null
    ? CIRCUMFERENCE * (1 - score / 100)
    : CIRCUMFERENCE;

  const color = score !== null ? scoreColor(score) : '#94a3b8';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          
          {t('Governance Score')}
        </CardTitle>
        <CardDescription>{t('HITL review rate vs correction frequency (30 days)')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center py-2">
        {loading ? (
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        ) : (
          <>
            <svg width="116" height="116" viewBox="0 0 140 140">
              {/* Track */}
              <circle
                cx="70" cy="70" r={RADIUS}
                fill="none"
                stroke="currentColor"
                strokeWidth="10"
                className="text-muted/30"
              />
              {/* Progress arc */}
              <circle
                cx="70" cy="70" r={RADIUS}
                fill="none"
                stroke={color}
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={dashOffset}
                transform="rotate(-90 70 70)"
                style={{ transition: 'stroke-dashoffset 0.6s ease' }}
              />
              {/* Score text */}
              <text
                x="70" y="66"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="28"
                fontWeight="700"
                fill={color}
              >
                {score ?? '—'}
              </text>
              <text
                x="70" y="90"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="11"
                fill="#94a3b8"
              >
                / 100
              </text>
            </svg>
            {score !== null && (
              <span
                className="mt-2 text-sm font-medium"
                style={{ color }}
              >
                {tx(scoreLabel(score))}
              </span>
            )}
            <div className="mt-3 grid grid-cols-3 gap-1.5 w-full text-center text-[11px] text-muted-foreground">
              <div className="rounded bg-red-50 dark:bg-red-950/20 px-2 py-1.5">
                <div className="font-semibold text-red-500">&lt;60</div>
                <div>{t('At Risk')}</div>
              </div>
              <div className="rounded bg-amber-50 dark:bg-amber-950/20 px-2 py-1.5">
                <div className="font-semibold text-amber-500">60–79</div>
                <div>{t('Monitor')}</div>
              </div>
              <div className="rounded bg-emerald-50 dark:bg-emerald-950/20 px-2 py-1.5">
                <div className="font-semibold text-emerald-500">80+</div>
                <div>{t('Good')}</div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default GovernanceScore;
